import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { FileIcon, DownloadIcon, AlertCircle } from "lucide-react";
import { useThresholdUpdateCsvParser } from "../hooks/useCsvParser";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { trpc } from "@/lib/trpc";
import { useLocation } from "@/contexts/LocationContext";
import { toast } from "@/hooks/use-toast";

// Sample CSV template content
const templateCSV = `sku,notify
TSHIRT-BLK-L,10
HOODIE-RED-M,5
PANTS-BLUE-S,15`;

interface BulkThresholdUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const BulkThresholdUploadModal: React.FC<BulkThresholdUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { selectedLocation } = useLocation();
  const utils = trpc.useUtils();

  // Use the threshold CSV parser hook
  const {
    file,
    parsedData,
    parseErrors,
    isParsing,
    fileInputRef,
    handleFileChange,
    resetParser,
    downloadTemplate,
    triggerFileSelect
  } = useThresholdUpdateCsvParser();

  // tRPC mutation for bulk threshold update
  const bulkUpdateThresholdsMutation = trpc.inventory.bulkUpdateThresholds.useMutation({
    onSuccess: (data) => {
      if (selectedLocation) {
        void utils.inventory.getProducts.invalidate({ locationId: selectedLocation.id });
      }

      let description = `Successfully updated ${data.updated} thresholds`;
      if (data.notFound.length > 0) {
        description += ` (${data.notFound.length} SKUs not found)`;
      }

      toast({
        title: "Thresholds Updated",
        description,
      });

      resetForm();
      onSuccess();
      onClose();
    },
    onError: (error) => {
      toast({
        title: "Update Failed",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await handleFileChange(e);
  };

  // Enter preview mode once parsing has produced rows or errors for a selected file
  React.useEffect(() => {
    if (file && (parsedData.length > 0 || parseErrors.length > 0)) {
      setIsPreviewMode(true);
    }
  }, [file, parsedData, parseErrors]);

  const handleDownloadTemplate = () => {
    downloadTemplate(templateCSV, 'threshold_update_template.csv');
  };

  const handleUpload = async () => {
    if (parsedData.length === 0 || !selectedLocation) return;

    try {
      setIsUploading(true);

      await bulkUpdateThresholdsMutation.mutateAsync({
        locationId: selectedLocation.id,
        updates: parsedData.map(row => ({
          sku: row.sku,
          notify: row.notify,
        })),
      });
    } catch (_error) {
      // Error is handled by the mutation's onError callback
    } finally {
      setIsUploading(false);
    }
  };

  const resetForm = () => {
    resetParser();
    setIsPreviewMode(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Update Thresholds</DialogTitle>
          <DialogDescription>
            Upload a CSV file to update notify thresholds for multiple SKUs at once.
            The CSV should have <code>sku</code> and <code>notify</code> columns.
          </DialogDescription>
        </DialogHeader>

        {/* Show errors if any */}
        {parseErrors.length > 0 && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Parsing Errors</AlertTitle>
            <AlertDescription>
              <ul className="list-disc list-inside">
                {parseErrors.slice(0, 5).map((error, index) => (
                  <li key={index} className="text-sm">{error}</li>
                ))}
                {parseErrors.length > 5 && (
                  <li className="text-sm">...and {parseErrors.length - 5} more errors</li>
                )}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {!isPreviewMode ? (
          <div className="space-y-6 py-4">
            <div className="flex justify-between items-center">
              <div className="space-y-1">
                <h3 className="text-sm font-medium">Download Template</h3>
                <p className="text-sm text-muted-foreground">
                  Use our template to ensure your CSV has the correct format
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={handleDownloadTemplate}
              >
                <DownloadIcon className="h-4 w-4" />
                Template
              </Button>
            </div>

            <div className="border rounded-md p-8 text-center space-y-4">
              <div className="flex justify-center">
                <FileIcon className="h-10 w-10 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-medium">
                  Drag and drop your CSV file, or click to browse
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Only CSV files are supported
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                disabled={isParsing}
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={triggerFileSelect}
                disabled={isParsing}
              >
                {isParsing ? "Processing..." : "Select File"}
              </Button>
              {file && (
                <p className="text-sm text-muted-foreground">
                  Selected: {file.name}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="py-4">
            <h3 className="text-sm font-medium mb-2">
              Preview ({parsedData.length} threshold updates)
            </h3>
            <div className="border rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted border-b">
                    <th className="px-4 py-2 text-left font-medium">SKU</th>
                    <th className="px-4 py-2 text-left font-medium">Notify Threshold</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedData.slice(0, 100).map((row, index) => (
                    <tr key={index} className="border-b">
                      <td className="px-4 py-2 font-mono">{row.sku}</td>
                      <td className="px-4 py-2">{row.notify}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {parsedData.length > 100 && (
              <p className="text-xs text-muted-foreground mt-2">
                Showing first 100 of {parsedData.length} updates
              </p>
            )}
          </div>
        )}

        <DialogFooter className="pt-4">
          {isPreviewMode ? (
            <>
              <Button
                variant="outline"
                onClick={resetForm}
                disabled={isUploading}
              >
                Back
              </Button>
              <Button
                className="bg-threadheads-yellow text-black hover:bg-threadheads-yellow/90"
                onClick={handleUpload}
                disabled={isUploading || parsedData.length === 0}
              >
                {isUploading ? "Updating..." : `Update ${parsedData.length} Thresholds`}
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkThresholdUploadModal;
